// liveHand.ts - the generated-hand adaptor (GH2). One reusable seam from a `GeneratedScenario`
// (scenario/generate.ts) to everything a UI surface renders: the felt, the action ramp, the
// recommendation, and a per-street grader. GH3 (Observe / Diagnose / Solo) binds to this; GH2 only
// builds the adaptor + proves the option-set correctness core.
//
// THE CORRECTNESS CORE (Joe's "never a fake option, never a missing one" guarantee): the action ramp
// is derived from `DecisionPoint.legal` (hand/legal.ts) - the engine's exact per-state legal set - and
// NEVER from a fixed template. So the UI can neither manufacture a choice the rules forbid nor drop one
// the rules allow. See `legalToActionRamp` below and liveHand.test.ts for the pinned assertions.
//
// What this file does NOT do: it does not compute the recommendation or the grade itself. Those need the
// D1 archetype/range/rule data injected (the GH1 live-advice chain: selectVillainStartingRange →
// narrowThroughStreets → assembleLiveContext → recommend → gradeDecision). GH3 owns that data wiring and
// passes a `LiveAdvisor` in; here the adaptor stays a pure structural mapping so the option-set guarantee
// is testable at $0 with no data.
import type {
  ActionKind,
  CardIndex,
  DecisionPoint,
  HandState,
  LegalAction,
  Position,
  RecommendedAction,
} from '@postflop/schema';
import type { DecisionGrade, GeneratedScenario, UserDecision } from '@postflop/engine';
import { type FeltView, type SeatView, ringFor, POSITION_LABEL, money } from './bridge';

// ---------------------------------------------------------------------------------------------------
// (b) legalToActionRamp - the correctness core: legal kinds → labeled ramp actions.
// ---------------------------------------------------------------------------------------------------

/** One button in the action ramp. Structurally what `diagnose/ActionRamp` renders (`key`/`label`/`sub`/
 *  `cls`), but `key` spans ALL six legal kinds (the diagnose `ActionDef` is narrowed to fold/call/raise
 *  for its authored facing-a-bet hand). `cls` is the style bucket: fold; passive (check/call); aggressive
 *  (bet/raise/jam). `min`/`max` carry the engine's real sizing bounds through to GH3's bet-math builder.
 *  GH3 widens ActionRamp's prop to accept this and routes bet/raise into the manual sizing tier. */
export interface RampAction {
  key: ActionKind;
  label: string;
  sub: string;
  cls: 'fold' | 'call' | 'raise';
  /** sizing bounds from the LegalAction (chips). Present for call/bet/raise/jam; absent for fold/check. */
  min?: number;
  max?: number;
}

/** Style bucket per kind: fold is its own; check/call are passive; bet/raise/jam are aggressive. */
const CLS_OF: Record<ActionKind, RampAction['cls']> = {
  fold: 'fold',
  check: 'call',
  call: 'call',
  bet: 'raise',
  raise: 'raise',
  jam: 'raise',
};

/** Plain-English label + sub per kind. `amount` is the call/raise-to reference for the label. */
const labelFor = (kind: ActionKind, amount: number | undefined): { label: string; sub: string } => {
  switch (kind) {
    case 'fold':
      return { label: 'Fold', sub: 'give it up' };
    case 'check':
      return { label: 'Check', sub: 'see the next card' };
    case 'call':
      return { label: amount ? `Call ${money(amount)}` : 'Call', sub: 'pay to continue' };
    case 'bet':
      return { label: 'Bet', sub: 'build the number' };
    case 'raise':
      return { label: 'Raise', sub: 'raise it up' };
    case 'jam':
      return { label: 'All in', sub: 'shove the stack' };
  }
};

/**
 * Map the engine's `legal` set to the labeled ramp - the ONLY place the UI's option set is decided, and
 * it is decided ENTIRELY by `legal`. The single normalization: KEEP `jam` iff a `bet` or `raise` is also
 * legal; otherwise DROP it.
 *
 * Why that rule is exactly right. `jam` is never an independent action - it is the all-in top of a
 * bet/raise continuum (a bet/raise sized to the whole stack). `hand/legal.ts` now gates `jam` on the
 * same right as `raise`/`bet`, so a `jam` only ever appears alongside a `bet` or `raise`. This filter is
 * therefore defense-in-depth: when neither `bet` nor `raise` is legal, hero can only check/call/fold, and
 * any stray `jam` would be one of two non-options - (i) the SAME chips as an all-in call (a river shove
 * hero can only call - `jam` amount == `call` amount), or (ii) an ILLEGAL short re-shove that did not
 * reopen action (`hasOpenRaiseRight` false yet `jam` amount > `call`). Both are dropped. When a
 * `bet`/`raise` IS legal, `jam` is the genuine all-in affordance and is kept. Result: a not-facing street
 * → {check, bet, jam}; facing-with-room → {fold, call, raise, jam}; a river all-in OR a no-raise-right
 * spot → {fold, call}. Never an invented option, never an illegal one, never a legal-and-distinct one
 * dropped. (Since `hand/legal.ts:40`'s gate landed, the engine no longer lists such a jam; this filter
 * keeps the UI guarantee holding regardless of upstream changes.)
 */
export const legalToActionRamp = (legal: LegalAction[]): RampAction[] => {
  const hasBetOrRaise = legal.some((l) => l.kind === 'bet' || l.kind === 'raise');

  return legal
    .filter((l) => l.kind !== 'jam' || hasBetOrRaise)
    .map((l) => {
      const { label, sub } = labelFor(l.kind, l.min);
      const action: RampAction = { key: l.kind, label, sub, cls: CLS_OF[l.kind] };
      if (l.min !== undefined) action.min = l.min;
      if (l.max !== undefined) action.max = l.max;
      return action;
    });
};

// ---------------------------------------------------------------------------------------------------
// (a) scenarioToFelt - real board / pot / seats over the existing ring geometry (do NOT rewrite the ring).
// ---------------------------------------------------------------------------------------------------

/** The seat leading into hero this street: the last villain to bet/raise/jam (the mint-highlight seat).
 *  Null when hero is not facing a bet (nobody has led this street). */
const aggressorSeat = (state: HandState): number | null => {
  const heroSeat = state.spec.heroSeat;
  for (let i = state.history.length - 1; i >= 0; i--) {
    const h = state.history[i];
    if (h.street !== state.street) break; // only THIS street's action highlights a live aggressor.
    const a = h.action;
    if (a.seat !== heroSeat && (a.kind === 'bet' || a.kind === 'raise' || a.kind === 'jam')) return a.seat;
  }
  return null;
};

/**
 * Build the `FeltView` a UI surface renders from a real generated hand. Board, pot, hero cards, hero
 * position/label, and the button all come from the engine `HandState`; the opponent pods reuse the
 * authored `RING` GEOMETRY (do NOT rewrite the ring) but are populated with each real villain's live
 * stack, folded state, and - for the current aggressor - the real bet chip. Villain display identity
 * (nickname / avatar) is still the ring's authored presentation seam; GH3/GH4 map real archetypes →
 * roster display names. Hero's hand descriptor is left off the label here (GH3 enriches it via
 * classifyCombo); GH2 keeps the felt honest on the parts that come straight from HandState.
 */
export const scenarioToFelt = (scenario: GeneratedScenario): FeltView => {
  const dp = scenario.decisionPoint;
  const state = dp.state;
  const hero = state.players.find((p) => p.seat === state.spec.heroSeat);
  if (!hero?.holeCards) throw new Error('scenarioToFelt: hero has no hole cards in the generated hand');

  const lead = aggressorSeat(state);
  const villains = state.players.filter((p) => p.seat !== state.spec.heroSeat);
  // TB3: SELECT the pods for this table size - never wrap. The old `RING[i % RING.length]` silently stacked
  // a 9-handed table's 8th villain on top of its 1st, which is why the felt was pinned to 7 seats
  // (`live/table.ts:35`). `ringFor` throws rather than double-stacks, and picks a balanced subset at 6/7.
  const ring = ringFor(villains.length);
  const seats: SeatView[] = villains.map((p, i) => {
    const geo = ring[i];
    const identity = p.archetypeId ?? geo.name;
    return {
      ...geo,
      seat: p.seat,
      name: identity,
      avatar: identity.charAt(0).toUpperCase(),
      stack: money(p.stack),
      out: p.folded,
      unread: false,
      active: !p.folded && p.seat === lead,
      bet: !p.folded && p.seat === lead && p.committedThisStreet > 0 ? money(p.committedThisStreet) : '',
      // TB3: the button is on a VILLAIN 8 hands out of 9 at a full ring. Straight from the engine's
      // `buttonSeat` - the same field `heroHasButton` reads, so the two can never disagree.
      button: p.seat === state.buttonSeat,
    };
  });

  return {
    seats,
    board: state.board as CardIndex[],
    pot: money(dp.pot),
    heroCards: hero.holeCards,
    heroLabel: `You, ${POSITION_LABEL[hero.position as Position]}`,
    heroHasButton: state.buttonSeat === state.spec.heroSeat,
  };
};

// ---------------------------------------------------------------------------------------------------
// (c) LiveHand - felt + legal + recommendation + a per-street grader hook, bundled for a UI surface.
// ---------------------------------------------------------------------------------------------------

/** The engine-data-backed advice for the decision, injected by GH3 (it owns the D1 archetype/range/rule
 *  wiring - the GH1 live-advice chain). Kept out of the adaptor so the option-set core stays $0-testable.
 *  `recommendation` is the engine's ranked action for this spot; `grade` scores the user's actual choice
 *  against it (outcome-independent - a correct fold grades UP even when it "would've won"). */
export interface LiveAdvisor {
  recommendation: RecommendedAction;
  grade: (user: UserDecision) => DecisionGrade;
}

/** Everything a UI surface needs to render + drive ONE live decision. `legal` is the raw engine set (so
 *  a surface can re-derive or validate); `ramp` is the labeled, de-duplicated button set actually
 *  rendered. `advisor` is the injected recommendation + grader; it is optional so a surface can bind the
 *  felt/ramp first and attach advice once the data chain resolves. */
export interface LiveHand {
  scenario: GeneratedScenario;
  decisionPoint: DecisionPoint;
  felt: FeltView;
  legal: LegalAction[];
  ramp: RampAction[];
  advisor?: LiveAdvisor;
}

/** Bundle a generated hand into a `LiveHand`. Pure/deterministic for felt+ramp+legal; the advisor is
 *  passed through untouched. This is the single call a UI surface makes per dealt decision. */
export const buildLiveHand = (scenario: GeneratedScenario, advisor?: LiveAdvisor): LiveHand => {
  const decisionPoint = scenario.decisionPoint;
  return {
    scenario,
    decisionPoint,
    felt: scenarioToFelt(scenario),
    legal: decisionPoint.legal,
    ramp: legalToActionRamp(decisionPoint.legal),
    advisor,
  };
};
